// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Tools → View's transparency for the dataset (owner,
 * 2026-10-12: every layer's opacity can change, and the dataset can be
 * hidden). The same slider as the palette dialog's beside Done, here so
 * a dataset playing alone (no overlays, so no date-track buttons), a
 * picture or a class map has it too, and the phone's View sheet, which
 * lends this section, gets it as well.
 *
 *   TRANSPARENCY                     30 %
 *   ■ ━━━━━●━━━━━━━━━━━━━━━━━━ □   👁
 *
 * Two lines, so the parked legend under it stays in view: the eye at the
 * end of the slider's line is the date track's eye (hide the dataset on
 * the globe, and show it again). The section hides itself while no
 * dataset is on the globe.
 */

import { t, tAttr, tHtml } from '../i18n'
import { formatTransparency, transparencyPercent } from '../services/overlayDisplay'

export interface ViewOpacityDeps {
  /** True while a dataset is on the main globe. */
  hasDataset(): boolean
  /** 1 opaque … 0 transparent. */
  opacity(): number
  hidden(): boolean
  setOpacity(opacity: number): void
  setHidden(hidden: boolean): void
}

export interface ViewOpacityHandle {
  /** Cheap when nothing changed, so the host may call it on a timer. */
  refresh(): void
}

const SVG = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"'
const ICON_EYE = `<svg ${SVG}><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`
const ICON_EYE_OFF = `<svg ${SVG}><path d="M9.9 5.2A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-2.6 3.5M6.6 6.6C3.9 8.4 2 12 2 12s3.6 7 10 7a9.5 9.5 0 0 0 5.4-1.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>`

export function mountViewOpacity(host: HTMLElement, deps: ViewOpacityDeps): ViewOpacityHandle {
  host.classList.add('tools-view-opacity')
  host.innerHTML = `
    <div class="tools-view-opacity-head">
      <h4 class="tools-menu-section-title" id="tools-view-opacity-title">${tHtml('palette.dialog.transparency')}</h4>
      <output class="ovl-palette-alpha-value" for="tools-view-alpha"></output>
    </div>
    <div class="ovl-palette-alpha-row">
      <span class="ovl-palette-alpha-end" data-end="opaque" title="${tAttr('palette.dialog.opaque')}" aria-hidden="true"></span>
      <input type="range" id="tools-view-alpha" class="ovl-palette-alpha-input" min="0" max="100" step="5" aria-labelledby="tools-view-opacity-title">
      <span class="ovl-palette-alpha-end" data-end="clear" title="${tAttr('palette.dialog.clear')}" aria-hidden="true"></span>
      <button type="button" class="tools-view-eye" id="tools-view-hide" aria-pressed="false"></button>
    </div>`
  const input = host.querySelector<HTMLInputElement>('#tools-view-alpha')!
  const value = host.querySelector<HTMLOutputElement>('.ovl-palette-alpha-value')!
  const hide = host.querySelector<HTMLButtonElement>('#tools-view-hide')!

  input.addEventListener('input', () => {
    const percent = Math.min(100, Math.max(0, Number(input.value) || 0))
    deps.setOpacity(1 - percent / 100)
    drawn = ''
    refresh()
  })
  hide.addEventListener('click', () => {
    deps.setHidden(!deps.hidden())
    drawn = ''
    refresh()
  })

  /** What the controls show, so a timer's refresh only touches the DOM on a change. */
  let drawn = ''

  function refresh(): void {
    const shown = deps.hasDataset()
    const percent = transparencyPercent(deps.opacity())
    const hidden = deps.hidden()
    const key = `${shown}:${percent}:${hidden}`
    if (key === drawn) return
    drawn = key
    host.hidden = !shown
    if (!shown) return
    const text = formatTransparency(percent)
    if (document.activeElement !== input) input.value = String(percent)
    input.setAttribute('aria-valuetext', text)
    value.textContent = text
    const label = t(hidden ? 'tools.opacity.hidden' : 'tools.opacity.hide')
    hide.setAttribute('aria-pressed', String(hidden))
    hide.setAttribute('aria-label', label)
    hide.title = label
    hide.innerHTML = hidden ? ICON_EYE_OFF : ICON_EYE
  }

  refresh()
  return { refresh }
}
